import type { SelectOption } from "@a-safe-digital/react-select"
import {
  getNames,
  getSupportedLanguages,
  registerLocale,
} from "i18n-iso-countries"
import daPack from "i18n-iso-countries/langs/da.json"
import dePack from "i18n-iso-countries/langs/de.json"
import enPack from "i18n-iso-countries/langs/en.json"
import esPack from "i18n-iso-countries/langs/es.json"
import frPack from "i18n-iso-countries/langs/fr.json"
import itPack from "i18n-iso-countries/langs/it.json"
import jaPack from "i18n-iso-countries/langs/ja.json"
import nlPack from "i18n-iso-countries/langs/nl.json"
import plPack from "i18n-iso-countries/langs/pl.json"
import svPack from "i18n-iso-countries/langs/sv.json"

/**
 * Country-name language packs mirroring the active Branch locales registered
 * in `app/frontend/shared/lib/i18n.ts`. When a new Branch goes live, register
 * its locale pack here so the Country dropdown localises country names for
 * those users.
 */
export const SUPPORTED_COUNTRY_LOCALES = [
  "da",
  "de",
  "en",
  "es",
  "fr",
  "it",
  "ja",
  "nl",
  "pl",
  "sv",
] as const

export type SupportedCountryLocale = (typeof SUPPORTED_COUNTRY_LOCALES)[number]

const FALLBACK_LOCALE: SupportedCountryLocale = "en"

type LocalePack = Parameters<typeof registerLocale>[0]

const localePacks: Record<SupportedCountryLocale, LocalePack> = {
  da: daPack,
  de: dePack,
  en: enPack,
  es: esPack,
  fr: frPack,
  it: itPack,
  ja: jaPack,
  nl: nlPack,
  pl: plPack,
  sv: svPack,
}

for (const locale of SUPPORTED_COUNTRY_LOCALES) {
  registerLocale(localePacks[locale])
}

const cachedOptions = new Map<string, SelectOption[]>()

/**
 * Localized "CODE - Name" country options, sorted by label in the user's
 * locale — the Country dropdown of the address/billing forms. Shared by the
 * React useCountryOptions hook and the Hotwire Site Invoicing tab's
 * contacts--country-options controller (CONEK-8263), so both surfaces list the
 * same countries in the same order.
 *
 * `language` is a BCP-47 tag (e.g. "fr-BE"); unsupported languages fall back
 * to English.
 */
export function buildCountryOptions(language: string): SelectOption[] {
  const normalizedLocale = language.split("-")[0]

  if (cachedOptions.has(normalizedLocale)) {
    return cachedOptions.get(normalizedLocale)!
  }

  const supportedLocale = getSupportedLanguages().includes(normalizedLocale)
    ? normalizedLocale
    : FALLBACK_LOCALE

  const countryNames = getNames(supportedLocale, { select: "official" })

  const options: SelectOption[] = Object.entries(countryNames).map(
    ([code, name]) => ({
      value: code,
      label: `${code} - ${name}`,
    }),
  )

  const sortedOptions = options.sort((a, b) =>
    a.label.localeCompare(b.label, normalizedLocale),
  )

  cachedOptions.set(normalizedLocale, sortedOptions)

  return sortedOptions
}
